import { Injectable, WritableSignal, signal, ElementRef } from '@angular/core';
import { Style } from '@local/client-contracts';
import { Tab, UButtonComponent } from '@local/ui-infra';
import { RouterService } from '@shared/services/router.service';
import { Subject, type BehaviorSubject } from 'rxjs';

export interface AdminPageButtonConfig {
  id: string;
  label?: string;
  typeElement?: string;
  styleType?: string;
  fontIcon?: string;
  pTooltip?: string;
  active$?: BehaviorSubject<boolean>;
  styles?: any;
  iconStyles?: any;
  versionNumber?: number;
  onClick: (buttonRef: UButtonComponent) => void;
}
export interface AdminPageConfig {
  title?: string;
  subTitle?: string;
  titleIcon?: { type: string; value: Style.Icon };
  buttonList?: AdminPageButtonConfig[];
  searchInputPlaceholder?: string;
  dynamicTabs?: Tab[];
  goBackRoute?: string;
  goBackTooltip?: string;
  sidebarVisible?: number;
  shouldCloseSideBar?: () => boolean;
  sidebarContent?: ElementRef;
}

@Injectable()
export class AdminService {
  private readonly BACK_SOURCES_ENABLED = ['all', 'linked'];
  private goBackRoute: string = '/admin/sources';

  adminData: WritableSignal<AdminPageConfig> = signal({});
  searchChange$: Subject<string> = new Subject();

  constructor(private routerService: RouterService) {}

  resetAll() {
    this.adminData.set({});
  }

  setButtonList(buttons: AdminPageButtonConfig[]) {
    const data = this.adminData();
    data.buttonList = buttons;
    this.adminData.set(data);
  }

  hasTab(tabId: string): boolean {
    return !!this.adminData().dynamicTabs?.find((t) => t.id === tabId);
  }

  addDynamicTab(tab: Tab) {
    const data = { ...this.adminData() };
    const newTabs = [...(data.dynamicTabs || [])];
    newTabs.push(tab);
    data.dynamicTabs = newTabs;
    this.adminData.set(data);
  }

  openSideBar(index: number, sidebarContent: ElementRef, shouldCloseSideBar?: () => boolean) {
    const data = this.adminData();
    data.sidebarVisible = index;
    data.sidebarContent = sidebarContent;
    data.shouldCloseSideBar = shouldCloseSideBar;
    this.adminData.set(data);
  }

  setSidebarVisible(index: number) {
    const data = { ...this.adminData() };
    data.sidebarVisible = index;
    this.adminData.set(data);
  }

  updateAdminData(data: AdminPageConfig) {
    if (data.goBackRoute) {
      this.setBackRoute();
      data.goBackRoute = this.goBackRoute;
    }
    const _data = { ...this.adminData(), ...data };
    this.adminData.set(_data);
  }

  private setBackRoute() {
    const source = this.routerService.queryParams?.source;
    if (this.BACK_SOURCES_ENABLED.includes(source)) {
      this.goBackRoute = `/admin/sources/${source}`;
    }
  }
}
